import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Download, Plus, Trash2, Edit2, Check, ArrowLeft } from 'lucide-react';

const Questionbus = () => {
  const token = localStorage.getItem('loginToken');
  const [companyData, setCompanyData] = useState(null);
  const [questionData, setQuestionData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const server_url = process.env.REACT_APP_API_URI;
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setCompanyData(decoded);
      } catch (error) {
        console.error('Invalid token:', error);
        setCompanyData(null);
      }
    } else {
      setCompanyData(null);
    }
  }, [token]);

  useEffect(() => {
    if (!companyData?.businessId) return;

    const fetchQuestions = async () => {
      try {
        const response = await axios.get(
          `${server_url}/businessQuestion/question/${companyData.businessId}`,
          { headers: { 'Content-Type': 'application/json' } },
        );
        setQuestionData(response.data.questions || []);
      } catch (error) {
        console.error('Error fetching questions:', error);
      }
    };

    fetchQuestions();
  }, [companyData?.businessId, server_url]);

  const handleEdit = (id, question, description) => {
    setEditingId(id);
    setEditText(question);
    setEditDescription(description);
  };

  const handleSave = async (id) => {
    try {
      await axios.put(`${server_url}/businessQuestion/update-question`, {
        businessId: companyData.businessId,
        questionId: id,
        question: editText,
        description: editDescription,
      });

      setQuestionData((prevData) =>
        prevData.map((q) =>
          q._id === id
            ? { ...q, question: editText, description: editDescription }
            : q,
        ),
      );

      setEditingId(null);
    } catch (error) {
      console.error('Error updating question:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?'))
      return;

    try {
      await axios.delete(`${server_url}/businessQuestion/delete-question`, {
        data: { businessId: companyData.businessId, questionId: id },
      });

      setQuestionData((prevData) => prevData.filter((q) => q._id !== id));
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) {
      alert('Please enter a question.');
      return;
    }

    try {
      const response = await axios.post(
        `${server_url}/businessQuestion/add-question`,
        {
          businessId: companyData.businessId,
          question: newQuestion,
          description: newDescription.trim() || '',
        },
      );

      setQuestionData([...questionData, response.data.question]);
      setNewQuestion('');
      setNewDescription('');
    } catch (error) {
      console.error('Error adding question:', error);
    }
  };

  const handlePullQuestions = async () => {
    if (!companyData?.businessId) return;
    try {
      const response = await axios.get(
        `${server_url}/business/add-category-questions/${companyData.businessId}`,
      );
      if (response.data.questions) {
        setQuestionData(response.data.questions);
        alert(
          response.data.addedQuestions?.length > 0
            ? `Successfully pulled ${response.data.addedQuestions.length} new questions!`
            : 'No new questions to pull.',
        );
      }
    } catch (error) {
      console.error('Error pulling questions:', error);
      alert(
        'Failed to pull questions. Ensure you have selected a category in your profile.',
      );
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pt-12 pb-20 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <button
              onClick={() => navigate('/businessprofile')}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-4 text-sm font-bold uppercase tracking-widest"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Profile
            </button>
            <h1 className="text-4xl font-black text-white tracking-tight">
              Rating Questions
            </h1>
            <p className="text-slate-400 mt-2">
              Customize the questions your customers answer when rating your
              business.
            </p>
          </div>

          <button
            onClick={handlePullQuestions}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-2xl transition-all shadow-lg shadow-blue-500/20 group"
          >
            <Download className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform" />
            Pull Category Questions
          </button>
        </div>

        {/* Question List */}
        <div className="space-y-4 mb-12">
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.3em] pl-2">
            Current Questions ({questionData.length})
          </h3>
          {questionData.length > 0 ? (
            <div className="grid grid-cols-1 gap-4">
              {questionData.map((item) => (
                <div
                  key={item._id}
                  className="group p-6 bg-slate-900 border border-slate-800 hover:border-slate-700 rounded-3xl transition-all shadow-xl"
                >
                  {editingId === item._id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full bg-slate-800 border-none rounded-xl p-4 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-blue-500"
                        placeholder="Edit Question Text"
                      />
                      <textarea
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        className="w-full bg-slate-800 border-none rounded-xl p-4 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-blue-500 h-24"
                        placeholder="Edit Description (Optional)"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSave(item._id)}
                          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white font-bold rounded-xl hover:bg-green-500 transition-colors"
                        >
                          <Check className="w-4 h-4" />
                          Save Changes
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="px-6 py-2 bg-slate-800 text-slate-300 font-bold rounded-xl hover:bg-slate-700 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <h2 className="text-xl font-bold text-white leading-tight">
                          {item.question}
                        </h2>
                        {item.description && (
                          <p className="text-slate-400 mt-2 text-sm leading-relaxed">
                            {item.description}
                          </p>
                        )}
                        {item.tag?.type === 'admin' && (
                          <span className="inline-block mt-3 px-2 py-0.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 text-[10px] font-black uppercase tracking-widest rounded-md">
                            Industry Standard
                          </span>
                        )}
                      </div>
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() =>
                            handleEdit(
                              item._id,
                              item.question,
                              item.description || '',
                            )
                          }
                          className="p-2 bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700 rounded-xl transition-all"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(item._id)}
                          className="p-2 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white rounded-xl transition-all"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 bg-slate-900/50 border-2 border-dashed border-slate-800 rounded-[2.5rem]">
              <div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-slate-600" />
              </div>
              <p className="text-slate-400 font-medium">
                No questions yet. Pull from your industry or add your own.
              </p>
            </div>
          )}
        </div>

        {/* Add New Question */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 rounded-[2.5rem] p-8 md:p-10 shadow-2xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 blur-[100px] pointer-events-none"></div>

          <h2 className="text-2xl font-black text-white mb-8">
            Create Custom Question
          </h2>
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                Question Text
              </label>
              <input
                type="text"
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-blue-500 transition-all"
                placeholder="e.g. How was the speed of service?"
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">
                Description (Optional)
              </label>
              <textarea
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 text-white placeholder:text-slate-600 focus:ring-2 focus:ring-blue-500 h-24 transition-all"
                placeholder="Add context to help customers answer..."
              />
            </div>
            <button
              onClick={handleAddQuestion}
              className="w-full py-4 bg-white text-slate-950 font-black text-lg rounded-2xl hover:bg-slate-100 transition-all shadow-xl shadow-white/5 active:scale-[0.98]"
            >
              Add Question
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Questionbus;
